import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GBP, ukDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "lucide-react";

const loadScript = (src) => new Promise((res, rej) => {
  if (document.querySelector(`script[src="${src}"]`)) return res();
  const s = document.createElement("script"); s.src = src; s.onload = res; s.onerror = rej; document.head.appendChild(s);
});

function SquareCardForm({ conf, amount, onSaved }) {
  const host = useRef(null);
  const gpay = useRef(null);
  const [card, setCard] = useState(null);
  const [payments, setPayments] = useState(null);
  const [apple, setApple] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let c, g, dead = false;
    (async () => {
      await loadScript(conf.environment === "sandbox" ? "https://sandbox.web.squarecdn.com/v1/square.js" : "https://web.squarecdn.com/v1/square.js");
      const p = window.Square.payments(conf.application_id, conf.location_id);
      c = await p.card(); await c.attach(host.current);
      if (dead) return;
      setPayments(p); setCard(c);
      const req = () => p.paymentRequest({ countryCode: "GB", currencyCode: "GBP", total: { amount: amount.toFixed(2), label: "Get Booked In monthly" } });
      try { g = await p.googlePay(req()); await g.attach(gpay.current, { buttonColor: "black", buttonSizeMode: "fill" }); gpay.current.onclick = () => save(g); } catch { /* not available on this device */ }
      try { setApple(await p.applePay(req())); } catch { /* not available on this device */ }
    })().catch((e) => setError(`Card form couldn't load: ${e.message || e}`));
    return () => { dead = true; c?.destroy?.(); g?.destroy?.(); };
  }, [conf.application_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (method) => {
    setBusy(true); setError("");
    try {
      const r = await method.tokenize();
      if (r.status !== "OK") throw new Error(r.errors?.[0]?.message || "Card details invalid");
      let verification_token = "";
      try { const v = await payments?.verifyBuyer(r.token, { amount: amount.toFixed(2), currencyCode: "GBP", intent: "STORE", billingContact: { countryCode: "GB" } }); verification_token = v?.token || ""; } catch { /* optional */ }
      const { data } = await api.post("/billing/square-card", { source_id: r.token, verification_token });
      toast.success(`${data.brand} •••• ${data.last4} saved`); onSaved();
    } catch (e) { setError(e.response ? formatApiError(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="square-card-form">
      <div ref={host} className="min-h-[90px]" />
      <Button data-testid="square-save-card" disabled={!card || busy} onClick={() => save(card)} className="w-full rounded-full bg-stone-900 h-11">{busy ? "Saving…" : "Save card"}</Button>
      {apple && <Button data-testid="square-apple-pay" variant="outline" disabled={busy} onClick={() => save(apple)} className="w-full rounded-full h-11">Apple Pay</Button>}
      <div ref={gpay} data-testid="square-google-pay" className="h-11" />
      {error && <p className="text-sm text-rose-600" data-testid="square-card-error">{error}</p>}
    </div>
  );
}

export function BillingPanel() {
  const [d, setD] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = useCallback(async () => { const { data } = await api.get("/billing"); setD(data); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const sid = params.get("setup_session");
    if (!sid) return;
    api.post(`/billing/stripe-setup/${sid}/complete`).then(({ data }) => { toast.success(`${data.brand} •••• ${data.last4} saved`); load(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setParams({ tab: "subscription" }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const { subscription: sub, pricing: p } = d;
  const stripeSetup = async () => {
    try { const { data } = await api.post("/billing/stripe-setup", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="billing-panel">
      <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
        <div className="flex items-center justify-between"><h2 className="font-semibold text-xl">Your plan</h2>
          <span data-testid="billing-status" className="text-xs font-medium px-3 py-1 rounded-full bg-[#F3EFEA] text-[#B07E4F] capitalize">{(sub.status || "").replace("_", " ")}</span></div>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-stone-500">Monthly price</span><span className="font-mono">{GBP(p.base_price)}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Staff</span><span>{p.staff_count} active · {p.staff_included} included</span></div>
          {p.extra_staff > 0 && <div className="flex justify-between"><span className="text-stone-500">Extra staff</span><span className="font-mono">{p.extra_staff} × {GBP(p.per_staff_price)}</span></div>}
          <div className="flex justify-between border-t border-stone-100 pt-2 font-semibold"><span>Total per month</span><span className="font-mono" data-testid="billing-total">{GBP(p.monthly_total)}</span></div>
          {sub.status === "trial" && <div className="flex justify-between"><span className="text-stone-500">Free trial ends</span><span className="font-medium">{ukDate(sub.trial_ends_at)}</span></div>}
          {sub.status !== "trial" && sub.current_period_end && <div className="flex justify-between"><span className="text-stone-500">Next payment</span><span>{ukDate(sub.current_period_end)}</span></div>}
        </div>
        {["expired", "suspended", "past_due"].includes(sub.status) && <p className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">Your booking page is {sub.status === "past_due" ? "at risk" : "paused"}. Add or update your card to carry on — we'll take payment straight away.</p>}
        {d.invoices.length > 0 && (<div className="mt-5"><h3 className="text-sm font-semibold mb-2">Payment history</h3>
          {d.invoices.map((i) => <div key={i.id} className="flex justify-between text-sm py-1"><span>{ukDate(i.created_at)}</span><span className="font-mono">{GBP(i.amount)}</span><span className={i.status === "paid" ? "text-emerald-700" : "text-rose-600"}>{i.status}</span></div>)}</div>)}
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
        <h2 className="font-semibold text-xl flex items-center gap-2"><CreditCard className="h-5 w-5" />Payment method</h2>
        {d.card ? <p className="mt-2 text-sm" data-testid="billing-card">{d.card.brand} •••• {d.card.last4} <span className="text-stone-500">· saved</span></p> : <p className="mt-2 text-sm text-stone-600">No card saved yet. Add one so your page stays live when your trial ends.</p>}
        <p className="text-xs text-stone-500 mt-1 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />Card, Apple Pay or Google Pay. You'll be charged {GBP(p.monthly_total)} monthly{sub.status === "trial" ? " after your trial" : ""}.</p>
        <div className="mt-4">
          {d.provider === "square" ? (d.square ? <SquareCardForm conf={d.square} amount={p.monthly_total} onSaved={load} /> : <p className="text-sm text-stone-500" data-testid="billing-not-ready">Card payments are being set up — nothing to do yet.</p>)
            : <Button data-testid="stripe-add-card" onClick={stripeSetup} className="w-full rounded-full bg-stone-900 h-11">{d.card ? "Update card" : "Add card"}</Button>}
        </div>
      </div>
    </div>
  );
}
